<script setup lang="ts">
    import { usePreferredReducedMotion } from '@vueuse/core';
    import { gsap } from 'gsap';

    type BackgroundColors = {
        topGradientStart: string;
        bottomGradientStart: string;
        mainShapeStart: string;
        mainShapeEnd: string;
        backLeft: string;
        backRightStart: string;
        backRightEnd: string;
        farRightStart: string;
        farRightEnd: string;
    };

    const backgroundViewBox = '0 0 1920 577';

    const backgroundColors: BackgroundColors = {
        topGradientStart: '#CD1719',
        bottomGradientStart: '#CD071E',
        mainShapeStart: '#CD071E',
        mainShapeEnd: '#F9E1E4',
        backLeft: '#CD1719',
        backRightStart: '#EF7911',
        backRightEnd: '#F9E1E4',
        farRightStart: '#EE7512',
        farRightEnd: '#FFFFFF'
    };

    const backgroundPaths = {
        topGradient: 'M1974 0H0.000244141V720.335H1974V0Z',
        bottomGradient: 'M1974 258.439H0V987H1974V258.439Z'
    };

    const shapes = {
        main: 'M-112.951 568.643C-128.705 929.506 151.056 1234.81 511.919 1250.57C872.781 1266.32 1178.09 986.562 1193.84 625.699C1209.6 264.837 929.837 -40.4718 568.975 -56.2263C208.112 -71.9808 -97.1965 207.781 -112.951 568.643Z',
        backRight:
            'M1299.12 248.833C1287.32 464.075 1457.99 648.44 1680.32 660.627C1902.65 672.814 2092.45 508.208 2104.25 292.966C2116.05 77.7234 1945.38 -106.641 1723.05 -118.828C1500.71 -131.015 1310.92 33.5908 1299.12 248.833Z',
        backLeft:
            'M1031.71 325.696C1296.1 61.305 1296.1 -367.33 1031.71 -631.722C767.315 -896.093 338.68 -896.093 74.289 -631.722C-190.102 -367.33 -190.102 61.305 74.289 325.696C338.68 590.088 767.315 590.088 1031.71 325.696Z',
        farRight:
            'M804 413C804 695.772 1045.99 925 1344.5 925C1643.01 925 1885 695.772 1885 413C1885 130.228 1643.01 -99 1344.5 -99C1045.99 -99 804 130.228 804 413Z'
    };

    const parallaxLayers = [
        { key: 'backLeft', damp: 0.03 },
        { key: 'backRight', damp: 0.05 },
        { key: 'farRight', damp: 0.04 },
        { key: 'main', damp: 0.07 }
    ] as const;

    const parallaxGroup = ref(null);
    const shapeMain = ref(null);
    const shapeBackLeft = ref(null);
    const shapeBackRight = ref(null);
    const shapeFarRight = ref(null);

    const mm = gsap.matchMedia();
    const preferredMotion = usePreferredReducedMotion();
    const shouldReduceMotion = computed(() => preferredMotion.value === 'reduce');

    onMounted(() => {
        mm.add(
            {
                desktop: '(min-width: 1024px)',
                tablet: '(min-width: 768px)',
                mobile: '(max-width: 767.98px)'
            },
            () => {
                if (shouldReduceMotion.value) {
                    return () => {};
                }

                const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
                tl.from(parallaxGroup.value, { opacity: 0, duration: 0.6 }).to(
                    shapeMain.value,
                    {
                        scale: '+=0.03',
                        duration: 0.7,
                        yoyo: true,
                        repeat: 1
                    },
                    '>-0.1'
                );

                const layerRefs = {
                    backLeft: shapeBackLeft.value,
                    backRight: shapeBackRight.value,
                    farRight: shapeFarRight.value,
                    main: shapeMain.value
                };

                const onMove = (e: MouseEvent) => {
                    const x = e.clientX - window.innerWidth / 2;
                    const y = e.clientY - window.innerHeight / 2;
                    parallaxLayers.forEach(({ key, damp }, i) => {
                        gsap.to(layerRefs[key], {
                            x: x * damp,
                            y: y * damp,
                            duration: 0.2,
                            ease: 'none',
                            overwrite: 'auto',
                            delay: i * 0.01
                        });
                    });
                };
                window.addEventListener('mousemove', onMove, { passive: true });

                return () => {
                    window.removeEventListener('mousemove', onMove);
                };
            }
        );
    });

    onBeforeUnmount(() => {
        mm.revert();
    });
</script>

<template>
    <div class="background animated">
        <div class="white-gradient"></div>

        <svg
            class="bg-stretch"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="backgroundViewBox"
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <defs>
                <linearGradient
                    id="nephrology-bg-top"
                    x1="985.037"
                    y1="111.86"
                    x2="990.023"
                    y2="742.306"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop :stop-color="backgroundColors.topGradientStart" />
                    <stop
                        offset="0.99"
                        stop-color="white"
                        stop-opacity="0"
                    />
                </linearGradient>
                <linearGradient
                    id="nephrology-bg-bottom"
                    x1="987"
                    y1="1039.07"
                    x2="987"
                    y2="250.883"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop :stop-color="backgroundColors.bottomGradientStart" />
                    <stop
                        offset="0.99"
                        stop-color="white"
                        stop-opacity="0"
                    />
                </linearGradient>
            </defs>

            <path
                :d="backgroundPaths.topGradient"
                fill="url(#nephrology-bg-top)"
            />
            <path
                :d="backgroundPaths.bottomGradient"
                fill="url(#nephrology-bg-bottom)"
            />
        </svg>

        <svg
            class="bg-svg"
            xmlns="http://www.w3.org/2000/svg"
            :viewBox="backgroundViewBox"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            <defs>
                <linearGradient
                    id="nephrology-main-shape"
                    x1="698"
                    y1="-11.0002"
                    x2="208"
                    y2="363"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop :stop-color="backgroundColors.mainShapeStart" />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.mainShapeEnd"
                    />
                </linearGradient>
                <linearGradient
                    id="nephrology-back-right"
                    x1="1722"
                    y1="76.9999"
                    x2="1962"
                    y2="471"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        :stop-color="backgroundColors.backRightStart"
                        stop-opacity="0.4"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.backRightEnd"
                        stop-opacity="0.3"
                    />
                </linearGradient>
                <linearGradient
                    id="nephrology-far-right"
                    x1="1088"
                    y1="690"
                    x2="1866"
                    y2="-99"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        :stop-color="backgroundColors.farRightStart"
                        stop-opacity="0.3"
                    />
                    <stop
                        offset="1"
                        :stop-color="backgroundColors.farRightEnd"
                        stop-opacity="0.1"
                    />
                </linearGradient>
            </defs>

            <g
                id="scene"
                ref="parallaxGroup"
            >
                <path
                    ref="shapeMain"
                    class="shape-main"
                    :d="shapes.main"
                    fill="url(#nephrology-main-shape)"
                />
                <path
                    ref="shapeBackRight"
                    class="shape-back-right"
                    :d="shapes.backRight"
                    fill="url(#nephrology-back-right)"
                />
                <path
                    ref="shapeBackLeft"
                    class="shape-back-left"
                    :d="shapes.backLeft"
                    :fill="backgroundColors.backLeft"
                    opacity="0.3"
                />
                <path
                    ref="shapeFarRight"
                    class="shape-far-right"
                    :d="shapes.farRight"
                    fill="url(#nephrology-far-right)"
                />
            </g>
        </svg>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/urology/urology-hero-background-animated.scss';

    .shape-main {
        transform-box: fill-box;
        transform-origin: 50% 50%;
    }
</style>
